import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { motion } from "framer-motion";
import {
  PlusIcon,
  FilterIcon,
  SearchIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  FolderIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/outline";
import { tasksAPI } from "../api/tasks";
import TaskForm from "../components/tasks/TaskForm";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const TasksPage = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const queryClient = useQueryClient();

  // Fetch tasks
  const { data, isLoading, error } = useQuery({
    queryKey: ["tasks", statusFilter, searchTerm, user?.role],
    queryFn: () => {
      const params = {
        status: statusFilter !== "all" ? statusFilter : undefined,
        search: searchTerm || undefined,
      };

      // If user is worker, only show assigned tasks
      if (user?.role === "worker") {
        params.assigned_to = user.id;
      }

      return tasksAPI.getAll(params);
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!user,
  });

  // Update status mutation (for drag and drop)
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => tasksAPI.updateStatus(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries(["tasks"]);
      toast.success("Task status updated");
    },
    onError: () => toast.error("Failed to update task status"),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => tasksAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["tasks"]);
      toast.success("Task deleted successfully");
    },
    onError: () => toast.error("Failed to delete task"),
  });

  const tasks = data?.data || [];

  // Group tasks by Project -> Stage
  const groupTasksByProjectAndStage = (taskList) => {
    const grouped = {};

    taskList.forEach((task) => {
      const projectKey = task.project_id;
      const stageKey = task.stage_id || "unassigned";
      const groupKey = `${projectKey}-${stageKey}`;

      if (!grouped[projectKey]) {
        grouped[projectKey] = {
          projectId: projectKey,
          projectName: task.project_name || `Project ${projectKey}`,
          stages: {},
        };
      }

      if (!grouped[projectKey].stages[stageKey]) {
        grouped[projectKey].stages[stageKey] = {
          stageId: stageKey,
          stageName: task.stage_name || "No Stage",
          tasks: [],
        };
      }

      grouped[projectKey].stages[stageKey].tasks.push(task);
    });

    // Convert to array format
    const result = [];
    Object.values(grouped).forEach((project) => {
      const stagesArray = Object.values(project.stages);
      result.push({
        ...project,
        stages: stagesArray,
      });
    });

    return result;
  };

  const groupedTasks = groupTasksByProjectAndStage(tasks);

  // Toggle group expansion
  const toggleGroup = (projectId, stageId) => {
    const key = `${projectId}-${stageId}`;
    setExpandedGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Check if group is expanded (default: expanded)
  const isGroupExpanded = (projectId, stageId) => {
    const key = `${projectId}-${stageId}`;
    return expandedGroups[key] !== undefined ? expandedGroups[key] : true;
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    if (source.droppableId !== destination.droppableId) {
      updateStatusMutation.mutate({
        id: parseInt(draggableId),
        status: destination.droppableId,
      });
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      deleteMutation.mutate(id);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "text-blue-500 bg-blue-50 dark:bg-blue-900/20",
      medium: "text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20",
      high: "text-orange-500 bg-orange-50 dark:bg-orange-900/20",
      critical: "text-red-500 bg-red-50 dark:bg-red-900/20",
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      todo: "badge-warning",
      in_progress: "badge-info",
      review: "badge-primary",
      done: "badge-success",
      blocked: "badge-danger",
    };
    return colors[status] || "badge-info";
  };

  const canManage =
    user?.role === "super_admin" ||
    user?.role === "project_manager" ||
    user?.role === "sub_manager";
  const canUpdateStatus =
    user?.role === "super_admin" ||
    user?.role === "project_manager" ||
    user?.role === "sub_manager" ||
    user?.role === "foreman";

  if (isLoading) return <LoadingSpinner />;

  // Render task card
  const renderTaskCard = (task) => (
    <Draggable key={task.id} draggableId={String(task.id)} index={0}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`bg-white dark:bg-slate-700 rounded-lg p-3 shadow-sm border border-slate-200 dark:border-slate-600 ${
            snapshot.isDragging ? "shadow-lg ring-2 ring-primary-500" : ""
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-slate-900 dark:text-white text-sm truncate">
                {task.title}
              </h4>
              {task.description && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                  {task.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Status Update Dropdown */}
              {canUpdateStatus && (
                <select
                  value={task.status || "todo"}
                  onChange={(e) => {
                    updateStatusMutation.mutate({
                      id: task.id,
                      status: e.target.value,
                    });
                  }}
                  className="text-xs px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary-500"
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="review">Review</option>
                  <option value="done">Done</option>
                  <option value="blocked">Blocked</option>
                </select>
              )}

              <button
                onClick={() => setViewingTask(task)}
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                title="View Details"
              >
                <EyeIcon className="h-3.5 w-3.5 text-slate-400" />
              </button>

              {canManage && (
                <>
                  <button
                    onClick={() => setEditingTask(task)}
                    className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                    title="Edit"
                  >
                    <PencilIcon className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                  <button
                    onClick={() => handleDelete(task.id)}
                    className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="h-3.5 w-3.5 text-red-400 hover:text-red-500" />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(task.priority)}`}
            >
              {task.priority || "Medium"}
            </span>
            <span className={`badge ${getStatusColor(task.status)}`}>
              {task.status || "Todo"}
            </span>
            {task.assigned_to_name && (
              <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                👤 {task.assigned_to_name}
              </span>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            📋 Tasks Board
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {user?.role === "worker"
              ? "View and update your assigned tasks"
              : "Manage tasks organized by project and stage"}
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            New Task
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <FilterIcon className="h-5 w-5 text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Status</option>
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="review">Review</option>
            <option value="done">Done</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>
      </div>

      {/* Tasks Display */}
      {tasks.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            No tasks found
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            {user?.role === "worker"
              ? "You have no tasks assigned yet"
              : "Create your first task to get started"}
          </p>
          {canManage && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary mt-4 inline-flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Create Task
            </button>
          )}
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="space-y-8">
            {groupedTasks.map((project) => (
              <div key={project.projectId} className="space-y-4">
                {/* Project Header */}
                <div className="flex items-center gap-3">
                  <FolderIcon className="h-5 w-5 text-primary-500" />
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {project.projectName}
                  </h2>
                  <span className="text-sm text-slate-400">
                    (
                    {project.stages.reduce((acc, s) => acc + s.tasks.length, 0)}{" "}
                    tasks)
                  </span>
                </div>

                {/* Stages within Project */}
                <div className="pl-4 border-l-2 border-primary-200 dark:border-primary-800 space-y-6">
                  {project.stages.map((stage) => {
                    const isExpanded = isGroupExpanded(
                      project.projectId,
                      stage.stageId,
                    );
                    const stageTasks = stage.tasks;

                    // Organize tasks by status for kanban within this stage
                    const columns = {
                      todo: {
                        id: "todo",
                        title: "To Do",
                        items: stageTasks.filter(
                          (t) => t.status === "todo" || !t.status,
                        ),
                      },
                      in_progress: {
                        id: "in_progress",
                        title: "In Progress",
                        items: stageTasks.filter(
                          (t) => t.status === "in_progress",
                        ),
                      },
                      review: {
                        id: "review",
                        title: "Review",
                        items: stageTasks.filter((t) => t.status === "review"),
                      },
                      done: {
                        id: "done",
                        title: "Done",
                        items: stageTasks.filter((t) => t.status === "done"),
                      },
                      blocked: {
                        id: "blocked",
                        title: "Blocked",
                        items: stageTasks.filter((t) => t.status === "blocked"),
                      },
                    };

                    return (
                      <div
                        key={`${project.projectId}-${stage.stageId}`}
                        className="space-y-3"
                      >
                        {/* Stage Header with Toggle */}
                        <div
                          className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 p-2 rounded-lg transition-colors"
                          onClick={() =>
                            toggleGroup(project.projectId, stage.stageId)
                          }
                        >
                          <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                            {isExpanded ? (
                              <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4" />
                            )}
                          </button>
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            {stage.stageName}
                          </span>
                          <span className="text-xs text-slate-400">
                            ({stageTasks.length} tasks)
                          </span>
                          {stageTasks.some((t) => t.status === "blocked") && (
                            <span className="badge badge-danger text-xs">
                              ⚠️ Blocked
                            </span>
                          )}
                        </div>

                        {/* Kanban Board - Only show if expanded */}
                        {isExpanded && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 pl-6">
                            {Object.values(columns).map((column) => (
                              <div
                                key={`${project.projectId}-${stage.stageId}-${column.id}`}
                                className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3"
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-medium text-slate-700 dark:text-slate-300 text-xs">
                                    {column.title}
                                  </h4>
                                  <span className="text-xs text-slate-500 dark:text-slate-400">
                                    {column.items.length}
                                  </span>
                                </div>
                                <Droppable
                                  droppableId={`${project.projectId}-${stage.stageId}-${column.id}`}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.droppableProps}
                                      className={`min-h-[100px] space-y-2 transition-colors ${
                                        snapshot.isDraggingOver
                                          ? "bg-primary-50 dark:bg-primary-900/10 rounded-lg p-1"
                                          : ""
                                      }`}
                                    >
                                      {column.items.map((task, index) => (
                                        <Draggable
                                          key={task.id}
                                          draggableId={`${task.id}`}
                                          index={index}
                                        >
                                          {(provided, snapshot) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              className={`bg-white dark:bg-slate-700 rounded-lg p-2 shadow-sm border border-slate-200 dark:border-slate-600 ${
                                                snapshot.isDragging
                                                  ? "shadow-lg ring-2 ring-primary-500"
                                                  : ""
                                              }`}
                                            >
                                              <div className="flex items-start justify-between gap-1">
                                                <div className="flex-1 min-w-0">
                                                  <h5 className="font-medium text-slate-900 dark:text-white text-xs truncate">
                                                    {task.title}
                                                  </h5>
                                                </div>
                                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                                  {canUpdateStatus && (
                                                    <select
                                                      value={
                                                        task.status || "todo"
                                                      }
                                                      onChange={(e) => {
                                                        e.stopPropagation();
                                                        updateStatusMutation.mutate(
                                                          {
                                                            id: task.id,
                                                            status:
                                                              e.target.value,
                                                          },
                                                        );
                                                      }}
                                                      className="text-[10px] px-1 py-0.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                    >
                                                      <option value="todo">
                                                        Todo
                                                      </option>
                                                      <option value="in_progress">
                                                        IP
                                                      </option>
                                                      <option value="review">
                                                        Review
                                                      </option>
                                                      <option value="done">
                                                        Done
                                                      </option>
                                                      <option value="blocked">
                                                        Blocked
                                                      </option>
                                                    </select>
                                                  )}
                                                  <button
                                                    onClick={() =>
                                                      setViewingTask(task)
                                                    }
                                                    className="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-600"
                                                  >
                                                    <EyeIcon className="h-3 w-3 text-slate-400" />
                                                  </button>
                                                  {canManage && (
                                                    <button
                                                      onClick={() =>
                                                        setEditingTask(task)
                                                      }
                                                      className="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-600"
                                                    >
                                                      <PencilIcon className="h-3 w-3 text-slate-400" />
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                                <span
                                                  className={`text-[10px] px-1.5 py-0.5 rounded-full ${getPriorityColor(task.priority)}`}
                                                >
                                                  {task.priority || "Med"}
                                                </span>
                                                {task.assigned_to_name && (
                                                  <span className="text-[10px] text-slate-500 dark:text-slate-400">
                                                    👤{" "}
                                                    {
                                                      task.assigned_to_name.split(
                                                        " ",
                                                      )[0]
                                                    }
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </Draggable>
                                      ))}
                                      {provided.placeholder}
                                      {column.items.length === 0 && (
                                        <div className="text-center py-4 text-xs text-slate-400 dark:text-slate-500">
                                          Empty
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </Droppable>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </DragDropContext>
      )}

      {/* ============================================ */}
      {/* MODALS */}
      {/* ============================================ */}

      {/* Create Task Modal */}
      {showCreateModal && (
        <TaskForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(["tasks"]);
            setShowCreateModal(false);
            toast.success("Task created successfully");
          }}
        />
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <TaskForm
          task={editingTask}
          projectId={editingTask.project_id}
          onClose={() => setEditingTask(null)}
          onSuccess={() => {
            queryClient.invalidateQueries(["tasks"]);
            setEditingTask(null);
            toast.success("Task updated successfully");
          }}
        />
      )}

      {/* View Task Modal */}
      {viewingTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Task Details
              </h2>
              <button
                onClick={() => setViewingTask(null)}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <span className="text-2xl">✕</span>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Title
                </p>
                <p className="font-medium text-slate-900 dark:text-white">
                  {viewingTask.title}
                </p>
              </div>
              {viewingTask.description && (
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Description
                  </p>
                  <p className="text-slate-700 dark:text-slate-300">
                    {viewingTask.description}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Status
                  </p>
                  <span
                    className={`badge ${getStatusColor(viewingTask.status)}`}
                  >
                    {viewingTask.status || "Todo"}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Priority
                  </p>
                  <span className="text-slate-700 dark:text-slate-300 font-medium">
                    {viewingTask.priority || "Medium"}
                  </span>
                </div>
              </div>
              {viewingTask.assigned_to_name && (
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Assigned To
                  </p>
                  <p className="text-slate-700 dark:text-slate-300">
                    {viewingTask.assigned_to_name}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {viewingTask.project_name && (
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Project
                    </p>
                    <p className="text-slate-700 dark:text-slate-300">
                      {viewingTask.project_name}
                    </p>
                  </div>
                )}
                {viewingTask.stage_name && (
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Stage
                    </p>
                    <p className="text-slate-700 dark:text-slate-300">
                      {viewingTask.stage_name}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
